"use client"

import * as React from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { ArrowDown01Icon, ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons"
import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker"

import { cn } from "@/lib/utils"
import { Button, buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>["variant"]
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "bg-background group/calendar p-3 [--cell-size:2.25rem] [[data-slot=card-content]_&]:bg-transparent [[data-slot=popover-content]_&]:bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("w-fit", defaultClassNames.root),
        months: cn(
          "relative flex flex-col gap-6 md:flex-row",
          defaultClassNames.months
        ),
        month: cn("flex w-full flex-col gap-3", defaultClassNames.month),
        nav: cn(
          "absolute inset-x-0 top-0 flex h-8 w-full items-center justify-between pointer-events-none",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-8 bg-transparent p-0 text-foreground/60 hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06] pointer-events-auto rounded-lg",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "size-8 bg-transparent p-0 text-foreground/60 hover:text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06] pointer-events-auto rounded-lg",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "flex h-8 w-full items-center justify-center text-[13px] font-medium text-foreground",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "has-focus:border-ring border-input shadow-xs has-focus:ring-ring/50 has-focus:ring-[3px] relative rounded-lg border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          "bg-popover absolute inset-0 opacity-0",
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          "select-none font-medium",
          captionLayout === "label"
            ? "text-[13px]"
            : "[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-lg pl-2 pr-1 text-sm [&>svg]:size-3.5",
          defaultClassNames.caption_label
        ),
        table: "w-full border-collapse",
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "text-muted-foreground w-(--cell-size) select-none text-[11px] font-medium leading-6",
          defaultClassNames.weekday
        ),
        week: cn("mt-0.5 flex w-full", defaultClassNames.week),
        week_number_header: cn(
          "w-(--cell-size) select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "text-muted-foreground select-none text-[0.8rem]",
          defaultClassNames.week_number
        ),
        // The range reads as one tint band behind the days: full on middle days,
        // half on the endpoints, rounded where a week row starts or ends.
        day: cn(
          "group/day relative size-(--cell-size) p-0 text-center text-[13px] focus-within:relative focus-within:z-20",
          "[&:has([data-range-middle=true])]:bg-primary-tint",
          "[&:has([data-range-start=true])]:bg-[linear-gradient(to_right,transparent_50%,var(--primary-tint)_50%)]",
          "[&:has([data-range-end=true])]:bg-[linear-gradient(to_left,transparent_50%,var(--primary-tint)_50%)]",
          "[&:has([data-range-start=true][data-range-end=true])]:bg-none",
          "first:[&:has([data-range-middle=true])]:rounded-l-full last:[&:has([data-range-middle=true])]:rounded-r-full",
          // Months end mid-week: round the band where it meets the blank days.
          "[&:has([data-range-middle=true]):has(+[data-outside=true])]:rounded-r-full [[data-outside=true]+&:has([data-range-middle=true])]:rounded-l-full",
          "first:[&:has([data-range-end=true])]:bg-none last:[&:has([data-range-start=true])]:bg-none",
          "[&:has([data-outside-day=true])]:!bg-none [&:has([data-outside-day=true])]:!bg-transparent",
          defaultClassNames.day
        ),
        range_start: cn(defaultClassNames.range_start),
        range_middle: cn(defaultClassNames.range_middle),
        range_end: cn(defaultClassNames.range_end),
        today: cn(defaultClassNames.today),
        outside: cn(
          "text-muted-foreground/40",
          defaultClassNames.outside
        ),
        disabled: cn(
          "text-muted-foreground/35",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          )
        },
        Chevron: ({ className, orientation }) => {
          if (orientation === "left") {
            return (
              <HugeiconsIcon icon={ArrowLeft01Icon} size={16} className={cn(className)} />
            )
          }

          if (orientation === "right") {
            return (
              <HugeiconsIcon icon={ArrowRight01Icon} size={16} className={cn(className)} />
            )
          }

          return (
            <HugeiconsIcon icon={ArrowDown01Icon} size={16} className={cn(className)} />
          )
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  const isOutside = Boolean(modifiers.outside)

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString()}
      data-outside-day={isOutside}
      data-selected-single={
        !isOutside &&
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={!isOutside && modifiers.range_start}
      data-range-end={!isOutside && modifiers.range_end}
      data-range-middle={!isOutside && modifiers.range_middle}
      data-today={!isOutside && modifiers.today}
      className={cn(
        "relative flex size-(--cell-size) items-center justify-center rounded-full p-0 text-[13px] font-normal leading-none tabular-nums transition-colors",
        "hover:bg-black/[0.05] dark:hover:bg-white/[0.07]",
        // Endpoints are ink: orange stays on the one Apply action.
        "data-[range-start=true]:bg-foreground data-[range-start=true]:text-background data-[range-start=true]:font-medium",
        "data-[range-end=true]:bg-foreground data-[range-end=true]:text-background data-[range-end=true]:font-medium",
        "data-[selected-single=true]:bg-foreground data-[selected-single=true]:text-background data-[selected-single=true]:font-medium",
        "data-[range-middle=true]:bg-transparent data-[range-middle=true]:hover:bg-black/[0.05] dark:data-[range-middle=true]:hover:bg-white/[0.07]",
        // Today: a small dot under the number, not a filled cell.
        "data-[today=true]:after:absolute data-[today=true]:after:bottom-1 data-[today=true]:after:size-1 data-[today=true]:after:rounded-full data-[today=true]:after:bg-current data-[today=true]:after:opacity-60",
        "group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-2 group-data-[focused=true]/day:ring-ring/50",
        "disabled:pointer-events-none",
        "data-[outside-day=true]:!bg-transparent data-[outside-day=true]:text-muted-foreground/40",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
